import { cn } from '@sunstone/ui';

type StatusConfig = {
  dot: string;
  text: string;
  label: string;
  pulse?: boolean;
};

const STATUS_MAP: Record<string, StatusConfig> = {
  // Healthy / Active
  TENANT_STATUS_ACTIVE: {
    dot: 'bg-green-500',
    text: 'text-green-700 dark:text-green-400',
    label: 'Active',
  },
  USER_STATUS_ACTIVE: {
    dot: 'bg-green-500',
    text: 'text-green-700 dark:text-green-400',
    label: 'Active',
  },
  DEPLOYMENT_STATUS_ACTIVE: {
    dot: 'bg-green-500',
    text: 'text-green-700 dark:text-green-400',
    label: 'Active',
  },
  WORKSPACE_STATUS_HEALTHY: {
    dot: 'bg-green-500',
    text: 'text-green-700 dark:text-green-400',
    label: 'Healthy',
  },

  // In-progress
  DEPLOYMENT_STATUS_CREATED: {
    dot: 'bg-amber-500',
    text: 'text-amber-700 dark:text-amber-400',
    label: 'Created',
  },
  DEPLOYMENT_STATUS_AWAITING_BOOTSTRAP: {
    dot: 'bg-blue-500',
    text: 'text-blue-700 dark:text-blue-400',
    label: 'Awaiting Bootstrap',
    pulse: true,
  },
  DEPLOYMENT_STATUS_PROVISIONING: {
    dot: 'bg-blue-500',
    text: 'text-blue-700 dark:text-blue-400',
    label: 'Provisioning',
    pulse: true,
  },
  DEPLOYMENT_STATUS_PROVISIONED: {
    dot: 'bg-blue-500',
    text: 'text-blue-700 dark:text-blue-400',
    label: 'Provisioned',
    pulse: true,
  },
  DEPLOYMENT_STATUS_PHASE_1_COMPLETE: {
    dot: 'bg-blue-500',
    text: 'text-blue-700 dark:text-blue-400',
    label: 'Phase 1 Complete',
    pulse: true,
  },
  DEPLOYMENT_STATUS_PHASE_2_COMPLETE: {
    dot: 'bg-blue-500',
    text: 'text-blue-700 dark:text-blue-400',
    label: 'Phase 2 Complete',
    pulse: true,
  },
  DEPLOYMENT_STATUS_BOOTSTRAP_TIMEOUT: {
    dot: 'bg-amber-500',
    text: 'text-amber-700 dark:text-amber-400',
    label: 'Bootstrap Timeout',
  },
  DEPLOYMENT_STATUS_MAINTENANCE: {
    dot: 'bg-muted-foreground',
    text: 'text-muted-foreground',
    label: 'Maintenance',
  },
  WORKSPACE_STATUS_PROVISIONING: {
    dot: 'bg-blue-500',
    text: 'text-blue-700 dark:text-blue-400',
    label: 'Provisioning',
    pulse: true,
  },
  WORKSPACE_STATUS_PENDING_DEPLOYMENT: {
    dot: 'bg-blue-500',
    text: 'text-blue-700 dark:text-blue-400',
    label: 'Pending Deployment',
    pulse: true,
  },
  WORKSPACE_UPGRADE_STATUS_SCHEDULED: {
    dot: 'bg-blue-500',
    text: 'text-blue-700 dark:text-blue-400',
    label: 'Upgrade Scheduled',
  },
  WORKSPACE_UPGRADE_STATUS_IN_PROGRESS: {
    dot: 'bg-blue-500',
    text: 'text-blue-700 dark:text-blue-400',
    label: 'Upgrading',
    pulse: true,
  },
  WORKSPACE_UPGRADE_STATUS_ROLLING_BACK: {
    dot: 'bg-amber-500',
    text: 'text-amber-700 dark:text-amber-400',
    label: 'Rolling Back',
    pulse: true,
  },

  // Degraded / failed
  DEPLOYMENT_STATUS_BOOTSTRAP_FAILED: {
    dot: 'bg-destructive',
    text: 'text-destructive',
    label: 'Bootstrap Failed',
  },
  DEPLOYMENT_STATUS_ERROR: { dot: 'bg-destructive', text: 'text-destructive', label: 'Error' },
  WORKSPACE_STATUS_DEGRADED: { dot: 'bg-destructive', text: 'text-destructive', label: 'Degraded' },
  WORKSPACE_STATUS_PROVISIONING_FAILED: {
    dot: 'bg-destructive',
    text: 'text-destructive',
    label: 'Provisioning Failed',
  },
  WORKSPACE_UPGRADE_STATUS_FAILED: {
    dot: 'bg-destructive',
    text: 'text-destructive',
    label: 'Upgrade Failed',
  },

  // Critical / blocked
  DEPLOYMENT_STATUS_MANAGEMENT_BLOCKED: {
    dot: 'bg-destructive ring-2 ring-destructive/30',
    text: 'text-destructive font-semibold',
    label: 'Management Blocked',
  },
  WORKSPACE_STATUS_UNAVAILABLE: {
    dot: 'bg-destructive ring-2 ring-destructive/30',
    text: 'text-destructive font-semibold',
    label: 'Unavailable',
  },
  WORKSPACE_STATUS_BLOCKED: {
    dot: 'bg-destructive ring-2 ring-destructive/30',
    text: 'text-destructive font-semibold',
    label: 'Blocked',
  },

  // Completed
  WORKSPACE_UPGRADE_STATUS_COMPLETE: {
    dot: 'bg-green-500',
    text: 'text-green-700 dark:text-green-400',
    label: 'Upgrade Complete',
  },
  WORKSPACE_UPGRADE_STATUS_ROLLED_BACK: {
    dot: 'bg-muted-foreground',
    text: 'text-muted-foreground',
    label: 'Rolled Back',
  },

  // Inactive / terminal
  TENANT_STATUS_INACTIVE: {
    dot: 'bg-muted-foreground',
    text: 'text-muted-foreground',
    label: 'Inactive',
  },
  USER_STATUS_INVITED: {
    dot: 'bg-amber-500',
    text: 'text-amber-700 dark:text-amber-400',
    label: 'Pending',
  },
  USER_STATUS_DISABLED: {
    dot: 'bg-muted-foreground',
    text: 'text-muted-foreground',
    label: 'Disabled',
  },
  USER_STATUS_DELETED: {
    dot: 'bg-destructive',
    text: 'text-destructive',
    label: 'Deleted',
  },
  DEPLOYMENT_STATUS_INACTIVE: {
    dot: 'bg-muted-foreground',
    text: 'text-muted-foreground',
    label: 'Inactive',
  },
  DEPLOYMENT_STATUS_DEPROVISIONING: {
    dot: 'bg-muted-foreground',
    text: 'text-muted-foreground',
    label: 'Deprovisioning',
    pulse: true,
  },
  WORKSPACE_STATUS_INACTIVE: {
    dot: 'bg-muted-foreground',
    text: 'text-muted-foreground',
    label: 'Inactive',
  },
  WORKSPACE_STATUS_DEPROVISIONING: {
    dot: 'bg-muted-foreground',
    text: 'text-muted-foreground',
    label: 'Deprovisioning',
    pulse: true,
  },
  WORKSPACE_STATUS_DEPROVISION_FAILED: {
    dot: 'bg-destructive',
    text: 'text-destructive',
    label: 'Deprovision Failed',
  },
  WORKSPACE_UPGRADE_STATUS_NONE: {
    dot: 'bg-muted-foreground/40',
    text: 'text-muted-foreground',
    label: 'No Upgrade',
  },

  // Release validation status
  RELEASE_VALIDATION_STATUS_PENDING: {
    dot: 'bg-muted-foreground/40',
    text: 'text-muted-foreground',
    label: 'Pending',
  },
  RELEASE_VALIDATION_STATUS_TESTING: {
    dot: 'bg-blue-500',
    text: 'text-blue-700 dark:text-blue-400',
    label: 'Testing',
    pulse: true,
  },
  RELEASE_VALIDATION_STATUS_PASSED: {
    dot: 'bg-green-500',
    text: 'text-green-700 dark:text-green-400',
    label: 'Passed',
  },
  RELEASE_VALIDATION_STATUS_FAILED: {
    dot: 'bg-destructive',
    text: 'text-destructive',
    label: 'Failed',
  },

  // Release approval status
  RELEASE_APPROVAL_STATUS_PENDING_APPROVAL: {
    dot: 'bg-amber-500',
    text: 'text-amber-700 dark:text-amber-400',
    label: 'Pending Approval',
    pulse: true,
  },
  RELEASE_APPROVAL_STATUS_LIVE: {
    dot: 'bg-green-500',
    text: 'text-green-700 dark:text-green-400',
    label: 'Live',
  },
  RELEASE_APPROVAL_STATUS_ROLLED_BACK: {
    dot: 'bg-muted-foreground',
    text: 'text-muted-foreground',
    label: 'Rolled Back',
  },

  // Release sync status
  RELEASE_SYNC_STATUS_NONE: {
    dot: 'bg-muted-foreground/40',
    text: 'text-muted-foreground',
    label: 'Never Synced',
  },
  RELEASE_SYNC_STATUS_IN_PROGRESS: {
    dot: 'bg-blue-500',
    text: 'text-blue-700 dark:text-blue-400',
    label: 'Syncing',
    pulse: true,
  },
  RELEASE_SYNC_STATUS_SUCCEEDED: {
    dot: 'bg-green-500',
    text: 'text-green-700 dark:text-green-400',
    label: 'Synced',
  },
  RELEASE_SYNC_STATUS_FAILED: {
    dot: 'bg-destructive',
    text: 'text-destructive',
    label: 'Sync Failed',
  },

  // Service health check status
  green: { dot: 'bg-green-500', text: 'text-green-700 dark:text-green-400', label: 'Healthy' },
  yellow: { dot: 'bg-amber-500', text: 'text-amber-700 dark:text-amber-400', label: 'Warning' },
  red: { dot: 'bg-destructive', text: 'text-destructive', label: 'Unhealthy' },
  timeout: { dot: 'bg-destructive', text: 'text-destructive', label: 'Timeout' },
  error: { dot: 'bg-destructive', text: 'text-destructive', label: 'Error' },
  suppressed: {
    dot: 'bg-muted-foreground',
    text: 'text-muted-foreground',
    label: 'Suppressed',
  },
};

const FALLBACK: StatusConfig = {
  dot: 'bg-muted-foreground/40',
  text: 'text-muted-foreground',
  label: 'Unknown',
};

/** Looks up the resolved StatusConfig for a given status key. */
export function getStatusConfig(status: string): StatusConfig {
  return STATUS_MAP[status] ?? FALLBACK;
}

// ─── StatusDot ───────────────────────────────────────────────────────────────

interface StatusDotProps {
  /** Proto status key e.g. "DEPLOYMENT_STATUS_ACTIVE" */
  status: string;
  /** Override tooltip / accessible label */
  title?: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const SIZE_CLASSES = {
  sm: 'h-1 w-1',
  md: 'h-1.5 w-1.5',
  lg: 'h-2 w-2',
};

/**
 * A standalone coloured dot that conveys health status at a glance.
 * Pulses for in-progress states. Includes a browser tooltip with the
 * status label for accessibility.
 */
export function StatusDot({ status, title, size = 'md', className }: StatusDotProps) {
  const config = getStatusConfig(status);
  return (
    <span
      role="img"
      title={title ?? config.label}
      aria-label={title ?? config.label}
      className={cn(
        'inline-block rounded-full flex-shrink-0',
        SIZE_CLASSES[size],
        config.dot,
        config.pulse && 'animate-pulse',
        className,
      )}
    />
  );
}

// ─── StatusBadge ─────────────────────────────────────────────────────────────

interface StatusBadgeProps {
  status: string;
  label?: string;
  className?: string;
}

/**
 * A status indicator that combines a StatusDot with a text label.
 * Prefer StatusDot alone in compact/card contexts where the label is
 * shown separately (e.g. the env label in a WorkspaceCard).
 */
export function StatusBadge({ status, label, className }: StatusBadgeProps) {
  const config = getStatusConfig(status);
  const displayLabel = label ?? config.label;

  return (
    <span
      className={cn('inline-flex items-center gap-1.5 text-sm font-medium', config.text, className)}
    >
      <StatusDot status={status} size="md" />
      {displayLabel}
    </span>
  );
}
